/**
 * @ken/module-quickbooks_wc_connector — frontend entry point.
 *
 * Mirrors @ken/module-products. Exports:
 *   - createRoutes(parent) — TanStack Router children factory
 *
 * The manifest is NOT exported here (CV-19). This file used to carry a
 * second, partial copy of the YAML; the build never read it, and shipping
 * it dragged a YAML parser into every visitor's first download.
 * modules/quickbooks_wc_connector/manifest.yaml is the only copy now.
 *
 * Routes mounted under `/$tenantSlug`:
 *   - settings/integrations/quickbooks       → QbwcSettingsPage (tabbed)
 *   - settings/integrations/quickbooks/setup → QbwcSetupWizardPage (modal)
 *   - settings/integrations/quickbooks/entities/$entityType
 *                                             → QbwcEntityDetailPage
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute } from "@tanstack/react-router";

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const QbwcEntityDetailPage = lazyScreen(
  () => import("./routes/qbwc-entity-detail-page"),
  "QbwcEntityDetailPage",
);
const QbwcSettingsPage = lazyScreen(
  () => import("./routes/qbwc-settings-page"),
  "QbwcSettingsPage",
);
const QbwcSetupWizardPage = lazyScreen(
  () => import("./routes/qbwc-setup-wizard-page"),
  "QbwcSetupWizardPage",
);

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const settingsRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "settings/integrations/quickbooks",
    component: QbwcSettingsPage,
  });

  const setupRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "settings/integrations/quickbooks/setup",
    component: QbwcSetupWizardPage,
  });

  const entityDetailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "settings/integrations/quickbooks/entities/$entityType",
    component: QbwcEntityDetailPage,
  });

  return [settingsRoute, setupRoute, entityDetailRoute];
};
